import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { vendorFetch } from '../../contexts/VendorAuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export default function VendorResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4" data-testid="reset-pw-invalid">
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8 text-center space-y-4">
        <XCircle size={36} className="text-red-500 mx-auto" />
        <div className="font-bold text-lg text-slate-900">Invalid Reset Link</div>
        <p className="text-slate-600 text-sm">This link is invalid or has expired.</p>
        <Link to="/partner/forgot-password"
          className="inline-block bg-[#3b4872] text-white px-6 py-2.5 rounded font-bold text-sm hover:bg-[#2d3856]">
          Request a new reset link →
        </Link>
      </div>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await vendorFetch('/partner/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: form.password }),
      });
      setDone(true);
      toast.success('Password updated!');
      setTimeout(() => nav('/partner/login'), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4" data-testid="reset-pw-page">
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8 space-y-5">
        <div className="text-center">
          <img src="/ellora-logo-blue.png" alt="Ellora EPC" className="h-9 w-auto mx-auto mb-3" />
          <div className="font-bold text-base text-slate-900">Set New Password</div>
          <div className="text-xs text-slate-400">Ellora EPC Vendor Portal</div>
        </div>

        {done ? (
          <div className="text-center space-y-3 py-4">
            <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
            <div className="font-bold text-slate-900 text-lg">Password updated!</div>
            <p className="text-sm text-slate-600">Redirecting to login…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {[
              { key: 'password', label: 'New Password' },
              { key: 'confirm', label: 'Confirm New Password' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form[key]}
                    onChange={e => setForm({ ...form, [key]: e.target.value })}
                    className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872] pr-10"
                    required minLength={8}
                    data-testid={`reset-${key}`}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-2.5 text-slate-400">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            ))}
            <button type="submit" disabled={loading}
              className="w-full bg-[#3b4872] hover:bg-[#2d3856] text-white py-2.5 rounded font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
